/* ============================================================
   Das Grundwörterbuch (SPEC §13.7) — Schale, Anmeldung, Profil, Nutzerverwaltung,
   Fehler und alles, was mehrere Bereiche teilen. Gehört P0.

   Tonlage aus .impeccable.md: ruhig, präzise, warm. Fachwörter bleiben Fachwörter
   (Stand, Abstimmung, Richtqualität). Die Oberfläche spricht niemanden direkt an —
   „Stand einfrieren", nicht „Friere den Stand ein"; Ausnahme ist die Anmeldung, die
   wörtlich aus projekte stammt (du-Form im Code-Satz, wie in der Mail).

   Werte sind Zeichenketten oder Funktionen (Platzhalter). Nur Deutsch (N12).
   ============================================================ */
import type { Lizenz, Rolle } from "../../api/types";

export const basis = {
  marke: { name: "LILASp", produkt: "Katalog" },

  allgemein: {
    laedt: "Wird geladen",
    speichern: "Sichern",
    gesichert: "Gesichert.",
    abbrechen: "Abbrechen",
    schliessen: "Schließen",
    loeschen: "Löschen",
    bearbeiten: "Bearbeiten",
    anlegen: "Anlegen",
    hinzufuegen: "Hinzufügen",
    zurueck: "Zurück",
    weiter: "Weiter",
    ja: "Ja",
    nein: "Nein",
    alle: "Alle",
    keine: "Keine",
    suchen: "Suchen",
    keineTreffer: "Keine Treffer.",
    optional: "freiwillig",
    pflicht: "Pflichtfeld",
    kopiert: "Kopiert",
    meldungKopieren: "Meldung kopieren",
    kommtBald: "Kommt bald",
  },

  nav: {
    bereiche: "Bereiche",
    kataloge: "Kataloge",
    bibliothek: "Bibliothek",
    admin: "Admin",
    profil: "Profil",
    konto: "Konto",
    abmelden: "Abmelden",
    themaHell: "Helles Thema",
    themaDunkel: "Dunkles Thema",
    zurUebersicht: "Zur Übersicht",
    modell: "Modell",
    zuordnen: "Zuordnen",
    importe: "Importe",
    editor: "Katalog",
    staende: "Stände",
  },

  admin: {
    bereiche: {
      nutzer: "Nutzer",
      bibliothek: "Bibliothek prüfen",
      kategorien: "Kategorien",
      regeln: "Regeln",
      ernte: "Ernte",
      maschinen: "Maschinen",
    },
    nutzer: {
      titel: "Nutzer",
      unterzeile: "Wer sich anmelden darf und wer verwaltet. Alle Angemeldeten bearbeiten alle Kataloge.",
      vorabAnlegen: "Vorab anlegen",
      vorabHinweis: "Wer eine Büroadresse hat, kann sich auch ohne Eintrag anmelden. Vorab anlegen lohnt sich, um gleich die Rolle zu setzen.",
      email: "E-Mail",
      anzeigename: "Name",
      rolle: "Rolle",
      aktiv: "Aktiv",
      stilllegen: "Stilllegen",
      freischalten: "Wieder freischalten",
      stillgelegt: "stillgelegt",
      nieAngemeldet: "noch nie angemeldet",
      zuletzt: (wann: string) => `zuletzt ${wann}`,
      anmeldungen: (n: number) => (n === 1 ? "1 Anmeldung" : `${n} Anmeldungen`),
      entsperren: "Sperre aufheben",
      entsperrenHinweis: "Nach 20 falschen Codes ist eine Adresse 24 Stunden gesperrt. Hier lässt sich die Sperre vorher aufheben.",
      entsperrt: (n: number) => (n === 0 ? "Es lag keine Sperre vor." : `Sperre aufgehoben (${n} Fehlversuche gelöscht).`),
      angelegt: "Angelegt.",
      geaendert: "Geändert.",
      leer: "Noch niemand angemeldet.",
    },
  },

  rollen: { admin: "Admin", nutzer: "Nutzer" } satisfies Record<Rolle, string>,

  anmelden: {
    titel: "Anmelden",
    // Wortlaut aus dem Canvas Anmeldung (§13: der Canvas gewinnt beim Wortlaut).
    unterzeile: "Wir schicken einen Code an deine Büroadresse.",
    konfigFehlt: "Die Anmeldung ist gerade nicht erreichbar. Wir versuchen es gleich noch einmal.",
    email: "E-Mail",
    emailPlatzhalter: "vorname@lilasp.de",
    codeSchicken: "Code schicken",
    codeTitel: "Code eingeben",
    codeUnterzeile: (mail: string) => `Wir haben einen sechsstelligen Code an ${mail} geschickt. Er gilt 10 Minuten.`,
    ziffer: (i: number, n: number) => `Ziffer ${i} von ${n}`,
    bleiben: "Angemeldet bleiben",
    bleibenHinweis: "30 Tage auf diesem Gerät",
    andereAdresse: "Andere Adresse",
    erneut: "Neuen Code schicken",
    erneutIn: (zeit: string) => `Neuen Code in ${zeit}`,
    domains: (d: string[]) => `Nur Adressen von ${d.join(" und ")}.`,
    pruefungFehler: "Die Prüfung von Cloudflare ist fehlgeschlagen. Bitte noch einmal.",
    startFehler: "Der Code ließ sich nicht schicken.",
    codeFehler: "Der Code stimmt nicht.",
  },

  profil: {
    titel: "Profil",
    unterzeile: "Name und Kürzel erscheinen in der Anwesenheit, wenn jemand am selben Katalog arbeitet.",
    anzeigename: "Name",
    anzeigenamePlatzhalter: "Vorname Nachname",
    kuerzel: "Kürzel",
    kuerzelHinweis: "Bis zu vier Zeichen. Ohne Kürzel stehen die Initialen da.",
    email: "E-Mail",
    rolle: "Rolle",
    seit: (wann: string) => `dabei seit ${wann}`,
    vorschau: "So sehen es die anderen",
    adminbereich: "Adminbereich",
  },

  lizenz: {
    name: { eigen: "eigen", erlaubt: "erlaubt", angefragt: "angefragt", unbekannt: "Lizenz unbekannt" } satisfies Record<Lizenz, string>,
    hinweis: {
      eigen: "Eigenes Bild des Büros.",
      erlaubt: "Die Nutzung ist erlaubt.",
      angefragt: "Die Erlaubnis ist angefragt.",
      unbekannt: "Rechte ungeklärt — in der Abgabe steht hier ein Platzhalter.",
    } satisfies Record<Lizenz, string>,
  },

  live: {
    unterbrochen: "Live-Verbindung unterbrochen — Änderungen anderer kommen verzögert.",
  },

  stummel: {
    titel: "Kommt bald",
    hinweis: "Dieser Bereich wird gerade gebaut.",
  },

  fehler: {
    nichtGefunden: "Diese Seite gibt es nicht.",
    zurStartseite: "Zur Übersicht",
    umgefallen: "Hier ist etwas schiefgegangen.",
    umgefallenHinweis: "Der Bildschirm konnte nicht gezeichnet werden. Neu laden hilft meistens.",
    neuLaden: "Neu laden",
    nochmal: "Noch einmal",
    erneut: "Erneut versuchen",
    ladenFehlgeschlagen: "Das ließ sich gerade nicht laden.",
    keinRecht: "Das darf nur ein Admin.",
    stoerungTitel: "Der Katalog antwortet gerade nicht",
    stoerungText: "Vielleicht wird gerade eine neue Fassung eingespielt. Wir fragen alle paar Sekunden nach — du bleibst angemeldet.",
  },
} as const;

export type Basis = typeof basis;
